import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '../components/ui/command';
import { LayoutDashboard, FolderKanban, Users, Calendar, FolderOpen, FileText, CreditCard, Bot, Bell, Settings, Search, Plus, Shield } from 'lucide-react';
import { demoProjects, demoPeople, demoFiles, demoContracts, demoPayments } from '../data/dummyData';

const navItems = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { label: 'Projects', path: '/projects', icon: FolderKanban },
  { label: 'People', path: '/people', icon: Users },
  { label: 'Specter', path: '/spectre', icon: Bot },
  { label: 'Calendar', path: '/calendar', icon: Calendar },
  { label: 'Files', path: '/files', icon: FolderOpen },
  { label: 'Contracts', path: '/contracts', icon: FileText },
  { label: 'Payments', path: '/payments', icon: CreditCard },
  { label: 'Notifications', path: '/notifications', icon: Bell },
  { label: 'Settings', path: '/settings', icon: Settings },
];

const actions = [
  { label: 'Create New Project', path: '/projects', icon: Plus },
  { label: 'Find Collaborator', path: '/people', icon: Search },
  { label: 'Ask Specter', path: '/spectre', icon: Bot },
];

const entityIndex = [
  ...demoProjects.map(p => ({ ainaa_id: p.ainaa_id, label: p.name, sub: p.project_type, path: `/projects/${p.project_id}`, icon: FolderKanban })),
  ...demoPeople.map(p => ({ ainaa_id: p.ainaa_id, label: p.name, sub: p.role, path: '/people', icon: Users })),
  ...demoFiles.map(f => ({ ainaa_id: f.ainaa_id, label: f.name, sub: f.file_type, path: '/files', icon: FolderOpen })),
  ...demoContracts.map(c => ({ ainaa_id: c.ainaa_id, label: c.title, sub: c.status, path: '/contracts', icon: FileText })),
  ...demoPayments.map(p => ({ ainaa_id: p.ainaa_id, label: p.title, sub: p.status, path: '/payments', icon: CreditCard })),
];

export default function SearchCommand() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const runCommand = useCallback((path) => {
    setOpen(false);
    setQuery('');
    navigate(path);
  }, [navigate]);

  const filteredEntities = useMemo(() => {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();
    return entityIndex.filter(e =>
      (e.ainaa_id && e.ainaa_id.toLowerCase().includes(q)) ||
      e.label.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [query]);

  return (
    <CommandDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(''); }}>
      <CommandInput
        placeholder="Search by name, AINAA ID, or command..."
        data-testid="search-command-input"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {filteredEntities.length > 0 && (
          <CommandGroup heading="AINAA Entities">
            {filteredEntities.map(e => (
              <CommandItem key={e.ainaa_id} onSelect={() => runCommand(e.path)} data-testid={`cmd-entity-${e.ainaa_id}`}>
                <e.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <span className="truncate">{e.label}</span>
                  <span className="text-[10px] text-muted-foreground ml-2 font-mono">{e.ainaa_id}</span>
                </div>
                <span className="text-[10px] text-muted-foreground capitalize ml-2">{e.sub}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {filteredEntities.length > 0 && <CommandSeparator />}

        <CommandGroup heading="Navigation">
          {navItems.map(item => (
            <CommandItem key={item.path} onSelect={() => runCommand(item.path)} data-testid={`cmd-${item.label.toLowerCase().replace(' ', '-')}`}>
              <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Actions">
          {actions.map(item => (
            <CommandItem key={item.label} onSelect={() => runCommand(item.path)} data-testid={`cmd-action-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
              <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
